import {
  type OneClickExpiredEvent,
  oneClickStatusMachine,
} from "@src/components/DefuseSDK/features/machines/oneClickStatusMachine"
import type { IntentDescription } from "@src/components/DefuseSDK/features/machines/swapIntentMachine"
import type {
  BaseTokenInfo,
  SupportedChainName,
  TokenInfo,
  TokenValue,
} from "@src/components/DefuseSDK/types/base"
import {
  type ActorRefFrom,
  assign,
  emit,
  enqueueActions,
  setup,
  stopChild,
} from "xstate"

type OneClickSettledEvent = {
  type: "ONE_CLICK_SETTLED"
  data: {
    depositAddress: string
    status: string
    tokenIn: TokenInfo
    tokenOut: TokenInfo
  }
}

export type TrackedWithdrawRef = {
  id: string
  intentHash: string
  depositAddress: string
  tokenIn: TokenInfo
  tokenOut: BaseTokenInfo
  // Source token; differs from tokenIn cross-asset. Used by ONE_CLICK_SETTLED dedup.
  paymentToken: TokenInfo
  amountIn: TokenValue
  amountOut: TokenValue
  recipient: string
  chainName: SupportedChainName
  recipientContactName: string | null
  createdAt: Date
  completedAt: Date | null
  seenOnWithdrawPage: boolean
  isConfidential: boolean
  actorRef: ActorRefFrom<typeof oneClickStatusMachine>
}

export type WithdrawTrackerContext = {
  withdrawRefs: TrackedWithdrawRef[]
}

export type RegisterWithdrawParams = {
  intentHash: string
  depositAddress: string
  tokenIn: TokenInfo
  tokenOut: BaseTokenInfo
  paymentToken: TokenInfo
  intentDescription: IntentDescription
  recipientContactName: string | null
  isConfidential?: boolean
}

export const withdrawTrackerMachine = setup({
  types: {
    context: {} as WithdrawTrackerContext,
    events: {} as
      | { type: "REGISTER_WITHDRAW"; params: RegisterWithdrawParams }
      | { type: "DISMISS_WITHDRAW"; id: string }
      | { type: "MARK_WITHDRAW_SEEN"; id: string }
      // Confidential deposits can expire; trackers never set a deadline, so this
      // never fires here — declared only so the actor's parent type is satisfied.
      | OneClickExpiredEvent
      | OneClickSettledEvent,
    emitted: {} as OneClickSettledEvent,
  },
  actors: {
    oneClickStatusActor: oneClickStatusMachine,
  },
  actions: {
    spawnWithdrawActor: assign({
      withdrawRefs: ({ context, event, spawn, self }) => {
        const { params } = event as {
          type: "REGISTER_WITHDRAW"
          params: RegisterWithdrawParams
        }
        if (params.intentDescription.type !== "withdraw") {
          return context.withdrawRefs
        }

        const id = params.intentHash
        if (context.withdrawRefs.some((w) => w.id === id)) {
          return context.withdrawRefs
        }

        const withdrawDescription = params.intentDescription as Extract<
          IntentDescription,
          { type: "withdraw" }
        >

        const actorRef = spawn("oneClickStatusActor", {
          id: `oneclick-${id}`,
          input: {
            parentRef: self,
            intentHash: params.intentHash,
            depositAddress: params.depositAddress,
            tokenIn: params.tokenIn,
            tokenOut: params.tokenOut,
            totalAmountIn: withdrawDescription.totalAmountIn,
            totalAmountOut: withdrawDescription.totalAmountOut,
            isConfidential: params.isConfidential,
          },
        })

        return [
          {
            id,
            intentHash: params.intentHash,
            depositAddress: params.depositAddress,
            tokenIn: params.tokenIn,
            tokenOut: params.tokenOut,
            paymentToken: params.paymentToken,
            amountIn: withdrawDescription.totalAmountIn,
            amountOut: withdrawDescription.totalAmountOut,
            recipient: withdrawDescription.recipient,
            chainName: withdrawDescription.tokenOutDeployment.chainName,
            recipientContactName: params.recipientContactName,
            createdAt: new Date(),
            completedAt: null,
            seenOnWithdrawPage: false,
            isConfidential: params.isConfidential === true,
            actorRef,
          },
          ...context.withdrawRefs,
        ]
      },
    }),
    dismissWithdrawActor: enqueueActions(({ enqueue, event }) => {
      const { id } = event as { type: "DISMISS_WITHDRAW"; id: string }
      enqueue(stopChild(`oneclick-${id}`))
      enqueue(
        assign({
          withdrawRefs: ({ context }) =>
            context.withdrawRefs.filter((w) => w.id !== id),
        })
      )
    }),
    markWithdrawSeen: assign({
      withdrawRefs: ({ context, event }) => {
        const { id } = event as { type: "MARK_WITHDRAW_SEEN"; id: string }
        return context.withdrawRefs.map((w) =>
          w.id === id ? { ...w, seenOnWithdrawPage: true } : w
        )
      },
    }),
    setCompletedAt: assign({
      withdrawRefs: ({ context, event }) => {
        if (event.type !== "ONE_CLICK_SETTLED") return context.withdrawRefs
        const now = new Date()
        return context.withdrawRefs.map((w) =>
          w.depositAddress === event.data.depositAddress &&
          w.completedAt == null
            ? { ...w, completedAt: now }
            : w
        )
      },
    }),
    passthroughEvent: emit((_, event: OneClickSettledEvent) => event),
  },
}).createMachine({
  id: "withdrawTracker",
  context: { withdrawRefs: [] },
  on: {
    REGISTER_WITHDRAW: { actions: "spawnWithdrawActor" },
    DISMISS_WITHDRAW: { actions: "dismissWithdrawActor" },
    MARK_WITHDRAW_SEEN: { actions: "markWithdrawSeen" },
    ONE_CLICK_SETTLED: {
      actions: [
        "setCompletedAt",
        { type: "passthroughEvent", params: ({ event }) => event },
      ],
    },
  },
})
